import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { BehaviorSubject, forkJoin, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AggregatedTableConfig, AggregatedTable, PivotMeta, TableRecord, Aggregation } from './aggregate-table.configs'



@Component({
    selector: 'aggregated-table',
    templateUrl: './aggregated-table.component.html',
    styleUrls: ['./aggregated-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AggregatedTableComponent<T> implements OnInit, OnDestroy {
    @Input() config!: AggregatedTableConfig<T>;
    @Input() refreshSubject = new BehaviorSubject<void>(null);

    @Output() dataLoaded = new EventEmitter<void>();

    public table: AggregatedTable | null = null;
    public isLoading = true;

    private destroy$ = new Subject<void>();

    constructor(private ref: ChangeDetectorRef) { }

    ngOnInit(): void {
        this.refreshSubject
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.build());
        this.build();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private build(): void {
        this.isLoading = true;
        this.ref.markForCheck();

        const needsPivot = !!this.config.pivot;
        const meta$ = needsPivot ? this.config.pivot!.meta$ : of<PivotMeta[]>([]);

        forkJoin({
            data: this.config.data$,
            meta: meta$
        })
            .pipe(
                map(({ data, meta }) => this.computeTable((data as any[]) ?? [], (meta ?? []) as PivotMeta[])),
                catchError(err => {
                    console.error('AggregatedTable error:', err);
                    return of<AggregatedTable>({
                        headers: this.config.baseHeaders?.length ? this.config.baseHeaders : ['Date'],
                        records: [],
                        totals: {}
                    });
                })
            )
            .subscribe(result => {
                this.table = result;
                this.isLoading = false;
                this.dataLoaded.emit();
                this.ref.markForCheck();
            });
    }

    private computeTable(rows: T[], metas: PivotMeta[]): AggregatedTable {
        const {
            fixedColumns,
            pivot,
            baseHeaders,
            dayKey,
            offsetHours = 0
        } = this.config;

        const headers: string[] = [...(baseHeaders?.length ? baseHeaders : ['Date'])];

        const byDay = this.groupBy(rows, r => this.dayLabel(dayKey(r), offsetHours));
        const dates = [...byDay.keys()].sort();

        let finalHeaders = [...headers];

        if (fixedColumns?.length) {
            finalHeaders.push(...fixedColumns.map(c => c.header));
        }

        let pivotHeaders: string[] = [];
        if (pivot) {
            const headerFor = pivot.headerForId ?? ((m: PivotMeta) => m.header);
            pivotHeaders = metas.map(m => headerFor(m));
            finalHeaders.push(...pivotHeaders);
        }

        const records: TableRecord[] = dates.map(date => {
            const group = byDay.get(date)!;

            const rec: any = { date };

            if (fixedColumns?.length) {
                for (const col of fixedColumns) {
                    const vals = group.map(col.selector).filter(this.isFiniteNumber) as number[];
                    const agg = this.aggregate(vals, col.aggregation);
                    rec[col.header] = this.formatOrBlank(agg, col.decimals ?? 2);
                }
            }

            if (pivot) {
                const dec = pivot.decimals ?? 2;
                const showBlank = pivot.showZeroAsBlank !== false;
                const sums = new Map<string, { sum: number; count: number }>();
                for (const row of group) {
                    const id = pivot.idSelector(row);
                    const v = Number(pivot.valueSelector(row) ?? 0);
                    if (!Number.isFinite(v)) continue;
                    const acc = sums.get(id) ?? { sum: 0, count: 0 };
                    acc.sum += v;
                    acc.count += 1;
                    sums.set(id, acc);
                }
                for (const meta of metas) {
                    const acc = sums.get(meta.id);
                    let value = 0;
                    if (acc) {
                        value = pivot.aggregation === 'avg'
                            ? (acc.count ? acc.sum / acc.count : 0)
                            : acc.sum;
                    }
                    rec[meta.header] = this.toCell(value, dec, showBlank);
                }
            }

            return rec;
        });

        const totals: Record<string, number> = {};
        for (const h of finalHeaders.slice(1)) {
            let sum = 0;
            for (const r of records) {
                const v = r[h];
                if (typeof v === 'number' && Number.isFinite(v)) sum += v;
            }
            totals[h] = this.round(sum, 2);
        }

        return { headers: finalHeaders, records, totals };
    }


    private groupBy<K>(arr: any[], keyFn: (x: any) => K): Map<K, any[]> {
        const m = new Map<K, any[]>();
        for (const it of arr) {
            const k = keyFn(it);
            const bucket = m.get(k);
            if (bucket) bucket.push(it); else m.set(k, [it]);
        }
        return m;
    }

    private dayLabel(input: string | Date | null | undefined, offsetHours: number): string {
        const d = input ? new Date(input) : new Date();
        if (offsetHours) d.setHours(d.getHours() + offsetHours);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    private aggregate(values: number[], agg: Aggregation): number {
        if (!values.length) return 0;
        if (agg === 'sum') return values.reduce((a, b) => a + b, 0);
        return values.reduce((a, b) => a + b, 0) / values.length;
    }

    private toCell(n: number, decimals: number, showBlankIfZero: boolean): number | '' {
        const r = this.round(n, decimals);
        if (showBlankIfZero && r === 0) return '';
        return r;
    }

    private round(n: number, dp: number): number {
        const f = Math.pow(10, dp);
        return Math.round((n + Number.EPSILON) * f) / f;
    }

    private isFiniteNumber(x: unknown): x is number {
        return typeof x === 'number' && Number.isFinite(x);
    }

    private formatOrBlank(n: number, decimals = 2, showBlankIfZero = true): number | '' {
        const r = this.round(n, decimals);
        return showBlankIfZero && r === 0 ? '' : r;
    }
}
